import React, { useState } from "react";
import { Icon } from "../icons";
import {
  avatarColor,
  fmtDate,
  fmtDateTime,
  fmtReminderWhen,
  initials,
  reminderSortKey,
  tagBadgeClass,
} from "../lib/format";
import { COLUMN_FILTERS, landedOverWeekend } from "../lib/columnFilter";
import { openJobPage } from "../lib/jobLink";
import { isDriverAssignable, fmtTime } from "../lib/driverDelivery";
import { attemptTag, defaultTabFor } from "../lib/jobStatus";
import { hourlyContractMissing, isHourlyJob } from "../lib/hourly";
import { describeRevision, revisionWhen } from "../lib/revisions";
import { hasReviewedTexts, unreviewedTexts } from "../lib/selectors";
import { crewTag, sitCheckInfo, sitConfirmedDate } from "../lib/sitLocal";
import { coverTag } from "../lib/buddy";
import { aiCallTag, cardAiCall } from "../lib/aiCalls";
import { callsDue } from "../lib/callSchedule";
import { useMcOps } from "../lib/McOpsContext";

export const StatCard = ({ icon, color, num, label }) => (
  <div className="stat-card">
    <div className={`stat-icon ${color}`}>
      <Icon name={icon} />
    </div>
    <div>
      <div className="num">{num}</div>
      <div className="label">{label}</div>
    </div>
  </div>
);

export const Badge = ({ kind = "gray", dot = true, children, style }) => (
  <span className={`badge ${kind}`} style={style}>
    {dot ? <span className="bdot" /> : null}
    {children}
  </span>
);

export const PanelEmpty = ({ children, style }) => (
  <div className="panel-empty" style={style}>
    {children}
  </div>
);

/**
 * All / Today / Past 7 days / This week chips for a kanban column's `toolbar`.
 * `options` swaps in another [key, label] list (the Reminders page uses three).
 */
export const ColumnFilter = ({ value, onChange, options = COLUMN_FILTERS }) =>
  options.map(([key, label]) => (
    <button
      type="button"
      className={`chip${value === key ? " active" : ""}`}
      key={key}
      onClick={() => onChange(key)}
    >
      {label}
    </button>
  ));

/**
 * "Reviewed with client": the MC called about the change and writes down what
 * was agreed. The note is required — it is the reason the job leaves the
 * Revised Confirmations queue. Clicks stay inside so a card does not open.
 */
export const ReviewNoteBox = ({ onReviewed, align = "left" }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const stop = (e) => e.stopPropagation();

  if (!open) {
    return (
      <button
        type="button"
        className="btn small success review-btn"
        onClick={(e) => {
          stop(e);
          setOpen(true);
        }}
      >
        <Icon name="check" />
        Reviewed with client
      </button>
    );
  }

  return (
    <div className={`review-box review-box-${align}`} onClick={stop}>
      <textarea
        autoFocus
        rows={2}
        placeholder="What was agreed with the client, and why the job changed…"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="review-box-actions">
        <button
          type="button"
          className="btn small"
          onClick={() => {
            setOpen(false);
            setText("");
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn small success"
          disabled={!text.trim()}
          onClick={() => {
            if (!text.trim()) return;
            onReviewed(text.trim());
            setOpen(false);
            setText("");
          }}
        >
          Save
        </button>
      </div>
    </div>
  );
};

/**
 * Why a job is a Revised Confirmation: each change since the client was
 * introduced or confirmed, with when it happened. Rendered on the card, in
 * the popup and on the job page so the wording is the same everywhere. With
 * `onReviewed` it also offers the action that clears the job.
 */
export const RevisionList = ({ job, className = "", onReviewed }) => {
  const revisions = job.revisions || [];
  if (!revisions.length) return null;
  return (
    <div className={`revision-list ${className}`.trim()}>
      <div className="revision-list-title">Changed after the client was confirmed</div>
      {revisions.map((r) => (
        <div className="revision-item" key={r.field}>
          <div className="revision-text">{describeRevision(r)}</div>
          {revisionWhen(r) ? <div className="revision-when">{revisionWhen(r)}</div> : null}
        </div>
      ))}
      {onReviewed ? (
        <div className="revision-actions">
          <ReviewNoteBox onReviewed={onReviewed} />
        </div>
      ) : null}
    </div>
  );
};

/** "Pickup BOL signed Sep 29, 9:12 AM" lines — the crew's checkpoints from the foreman app. */
export const BolLines = ({ job, className = "jc-line" }) => (
  <>
    {job.bolPickupSignedAt ? (
      <div className={className}>
        <Icon name="check" /> Pickup BOL signed {fmtDateTime(job.bolPickupSignedAt)}
      </div>
    ) : null}
    {job.bolDeliverySignedAt ? (
      <div className={className}>
        <Icon name="check" /> Delivery BOL signed {fmtDateTime(job.bolDeliverySignedAt)}
      </div>
    ) : null}
  </>
);

/** Driver Deliveries card block: ETA, driver/trip, note, and the Delivered / Unassign buttons. */
const DriverBlock = ({ job, actions }) => {
  const dd = job.driverDelivery || {};
  const stop = (fn) => (e) => {
    e.stopPropagation();
    if (fn) fn(job.id);
  };
  return (
    <div className="driver-info">
      <div className="di-row">
        <Icon name="clock" />
        <strong>
          ETA {fmtDate(dd.etaDate)}
          {dd.etaTime ? " · " + fmtTime(dd.etaTime) : ""}
        </strong>
      </div>
      {dd.driverName ? (
        <div className="di-row">
          <Icon name="truck" />
          Driver/Trip: {dd.driverName}
        </div>
      ) : null}
      {dd.note ? <div className="di-note">{dd.note}</div> : null}
      <div className="di-actions">
        <button
          type="button"
          className="btn small success"
          onClick={stop(actions && actions.markDriverDelivered)}
        >
          <Icon name="check" />
          Delivered
        </button>
        <button
          type="button"
          className="btn small"
          onClick={stop(actions && actions.unassignDriverDelivery)}
        >
          Unassign
        </button>
      </div>
    </div>
  );
};

export const JobCard = ({ job, tab, showRevisions = false, showPriority = false }) => {
  const { openJob, actions, state, timeOff } = useMcOps();
  const [dragging, setDragging] = useState(false);
  const cover = timeOff ? coverTag(job, timeOff.requests, (timeOff.me || {}).id) : null;
  const aiTag = aiCallTag(cardAiCall(job));
  const dueCalls = callsDue(job);
  const draggable = isDriverAssignable(job);
  // An escalation raised during the move stays on the card (open or resolved):
  // in Post Move it is why the job is a call, never an automation.
  const escalated = ((state && state.escalations) || []).some((e) => e.jobId === job.id);
  // Open reminders on this job, soonest first — shown on the card like the
  // revision lines, so the MC sees what is pending without opening the job.
  const reminders = ((state && state.reminders) || [])
    .filter((r) => r.jobId === job.id && !r.completed)
    .sort((a, b) => reminderSortKey(a).localeCompare(reminderSortKey(b)));

  if (job._isReminder) {
    return (
      <div className="job-card" style={{ borderLeftColor: "var(--warning)" }}>
        <div className="jc-top">
          <div className="jc-avatar" style={{ background: "var(--warning)" }}>
            <Icon name="bell" />
          </div>
          <div>
            <div className="jc-name">{job.clientName}</div>
            <div className="jc-zoho">{(job.tags && job.tags[0]) || ""}</div>
          </div>
        </div>
      </div>
    );
  }

  // `tab` may depend on the job (a long distance pickup opens another tab).
  const openTab = defaultTabFor(job, typeof tab === "function" ? tab(job) : tab);
  const weekend = landedOverWeekend(job.landedAt);
  const reviewed =
    showRevisions && actions && actions.markRevisionReviewed
      ? (text) => actions.markRevisionReviewed(job.id, text)
      : undefined;
  // New Daily Assignments: red = large or Super VIP, yellow = medium, blue = small.
  const priority = showPriority && job.priority ? ` prio-${job.priority}` : "";
  // The MC declared the client's last short text reviewed and nothing newer is waiting.
  const pendingTexts = unreviewedTexts(
    state,
    (((state && state.unanswered24h) || {}).texts || []).filter((t) => t.jobId === job.id),
  );
  const textsReviewed = hasReviewedTexts(job) && pendingTexts.length === 0;
  // SIT Local Confirmed: the crew tag colours the card (Miles, Asana 1219846172081526).
  const sitTag = job.status === "ld-sit-confirmed" ? crewTag(job) : null;
  const sitDelivery = sitTag ? sitConfirmedDate(job) : "";
  const sitCheck = sitTag ? sitCheckInfo(job) : null;

  return (
    <div
      className={`job-card type-${job.moveType}${priority}${sitTag ? ` sit-${sitTag.cls}` : ""}${dragging ? " dragging" : ""}`}
      onClick={() => openJob(job.id, openTab)}
      {...(draggable
        ? {
            draggable: true,
            title: "Drag to Driver Deliveries",
            onDragStart: (e) => {
              e.dataTransfer.setData("text/plain", job.id);
              e.dataTransfer.effectAllowed = "move";
              setDragging(true);
            },
            onDragEnd: () => setDragging(false),
          }
        : {})}
    >
      <div className="jc-top">
        <div className="jc-avatar" style={{ background: avatarColor(job.moveType) }}>
          {initials(job.clientName)}
        </div>
        <div>
          <div className="jc-name">
            {job.clientName}
            {isHourlyJob(job) ? <span className="hourly-pill">HOURLY</span> : null}
            {hourlyContractMissing(job) ? (
              <span className="hourly-pill unsigned" title="No hourly contract in Paperless yet — the estimate depends on it">
                CONTRACT NOT SIGNED
              </span>
            ) : null}
          </div>
          <div className="jc-zoho">{job.zohoId}</div>
        </div>
        <button
          type="button"
          className="jc-open"
          aria-label="Open in new tab"
          title="Open in new tab"
          onClick={(e) => {
            e.stopPropagation();
            openJobPage(job.id, openTab);
          }}
        >
          <Icon name="external" />
        </button>
      </div>
      {sitTag ? (
        <div className={`sit-flag ${sitTag.cls}`}>
          <Icon name={sitTag.cls === "ok" ? "check" : "alert"} />
          {sitTag.label}
        </div>
      ) : null}
      <div className="jc-tags">
        {dueCalls.map((c) => (
          <span
            key={c.code}
            className="badge"
            style={{
              background: c.overdue ? "var(--danger-light)" : "var(--warning-light)",
              color: c.overdue ? "var(--danger)" : "var(--warning)",
            }}
          >
            <span className="bdot" />
            {c.label}
          </span>
        ))}
        {aiTag ? (
          <span className={`badge ${aiTag.cls}`}>
            <span className="bdot" />
            {aiTag.label}
          </span>
        ) : null}
        {job.status === "weekly-assign" ? (
          <span className="badge weekly" title="More than 2 weeks out — introduction call first">
            <span className="bdot" />
            Weekly
          </span>
        ) : null}
        {weekend ? (
          <span className="badge weekend">
            <span className="bdot" />
            Landed over the weekend
          </span>
        ) : null}
        {escalated ? (
          <span className="badge escalated" title="An escalation was raised during this move">
            <span className="bdot" />
            Escalated
          </span>
        ) : null}
        {attemptTag(job) ? (
          <span className="badge attempt" title="Call attempts so far">
            <span className="bdot" />
            {attemptTag(job)}
          </span>
        ) : null}
        {job.riskFlags && job.riskFlags.length > 0 ? (
          <span className="badge risk-flagged" title="Risks identified on the confirmation call">
            <span className="bdot" />
            Risk flagged
          </span>
        ) : null}
        {textsReviewed ? (
          <span className="badge plain" title="The client's last text was reviewed — no action needed">
            <span className="bdot" />
            Reviewed
          </span>
        ) : null}
        {job.introCallDone ? (
          <span className="badge intro-done" title="Introduction call completed">
            <span className="bdot" />
            Intro done
          </span>
        ) : null}
        {job.sitLocalConfirmed ? (
          <span className="badge sit-confirmed" title="SIT Local was confirmed with the client">
            <span className="bdot" />
            SIT Local confirmed
          </span>
        ) : null}
        {job.inProgressCallDone ? (
          <span className="badge resolved" title="The MC completed the move-in-progress call">
            <span className="bdot" />
            Call completed
          </span>
        ) : null}
        {(job.tags || []).map((t, i) => (
          <span className={`badge ${tagBadgeClass(t)}`} key={`${t}-${i}`}>
            <span className="bdot" />
            {t}
          </span>
        ))}
      </div>
      <div className="jc-meta">
        <Icon name="activity" /> {job.moveFrom} → {job.moveTo}
      </div>
      {cover ? (
        <div className="jc-meta" style={{ margin: "6px 0 2px" }}>
          <span className="badge" style={{ background: "var(--warning-light)", color: "var(--warning)" }}>
            <Icon name="clock" />
            {cover.label}
          </span>
        </div>
      ) : null}
      {job.callBackRequested ? (
        <div className="jc-line">
          <Icon name="phone" /> Callback {fmtDate(job.callbackDate)}
          {job.callbackTime ? ` ${job.callbackTime}` : ""}
        </div>
      ) : null}
      {sitDelivery ? (
        <>
          <div className="jc-line">
            <Icon name="truck" /> <strong>Delivery {fmtDate(sitDelivery)}</strong>
          </div>
          {sitCheck ? (
            <div className={`jc-line sit-${sitCheck.cls}`}>
              <Icon name="bell" /> {sitCheck.label}
            </div>
          ) : null}
        </>
      ) : null}
      <BolLines job={job} />
      {job.status === "ld-driver-delivery" && job.driverDelivery ? (
        <DriverBlock job={job} actions={actions} />
      ) : null}
      {job.inProgressCallDone ? (
        <div className="jc-line">
          <Icon name="check" /> In-progress call completed
          {job.inProgressCallDoneAt ? ` · ${fmtDateTime(job.inProgressCallDoneAt)}` : ""}
        </div>
      ) : null}
      {reminders.length ? (
        <div className="jc-reminders">
          {reminders.map((r) => (
            <div className="jc-line jc-reminder" key={r.id}>
              <Icon name="bell" /> {r.text} · {fmtReminderWhen(r)}
            </div>
          ))}
        </div>
      ) : null}
      {showRevisions ? (
        <RevisionList job={job} className="jc-revisions" onReviewed={reviewed} />
      ) : null}
    </div>
  );
};

export const EscalationCard = ({ escalation, onToggle, onOpenJob }) => {
  const color = escalation.status === "open" ? "var(--danger)" : "var(--success)";
  return (
    <div
      className="job-card"
      style={{ borderLeftColor: color }}
      onClick={() => onToggle(escalation.id)}
    >
      <div className="jc-top">
        <div className="jc-avatar" style={{ background: color }}>
          <Icon name="alert" />
        </div>
        <div>
          <div className="jc-name">{escalation.jobLabel}</div>
        </div>
      </div>
      <div className="jc-tags">
        <Badge kind={escalation.status}>{escalation.status}</Badge>
      </div>
      <div className="jc-meta">{escalation.note}</div>
      <div className="chat-note">
        <Icon name="msg" />
        Ops/Sales group chat
      </div>
      {onOpenJob ? (
        <div className="toolbar-row" style={{ marginTop: 6 }}>
          <button
            type="button"
            className="btn small"
            onClick={(e) => {
              e.stopPropagation();
              onOpenJob(escalation);
            }}
          >
            <Icon name="external" />
            Open job
          </button>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Kanban column — mirrors kcolHTML() from the prototype, with the "screen N"
 * chip and an optional `toolbar` row (the column filter).
 */
export const KanbanColumn = ({
  title,
  jobs,
  color = "var(--primary)",
  tab,
  screenNo,
  hideCount = false,
  emptyLabel = "No jobs",
  renderItem,
  toolbar,
  showRevisions = false,
  showPriority = false,
  dropZone,
  onDropJob,
}) => {
  const [dropOver, setDropOver] = useState(false);
  const dropProps = dropZone
    ? {
        "data-dropzone": dropZone,
        onDragOver: (e) => {
          e.preventDefault();
          setDropOver(true);
        },
        onDragLeave: (e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setDropOver(false);
        },
        onDrop: (e) => {
          e.preventDefault();
          setDropOver(false);
          const id = e.dataTransfer.getData("text/plain");
          if (id && onDropJob) onDropJob(id, dropZone);
        },
      }
    : {};
  return (
    <div className={`kcol${dropOver ? " drop-over" : ""}`} {...dropProps}>
      <div className="kcol-accent" style={{ background: color }} />
      <div className="kcol-head">
        <span className="htxt">
          <span className="kcol-dot" style={{ background: color }} />
          {screenNo ? <span className="kcol-screenno">{screenNo}</span> : null}
          {title}
        </span>
        <span className="kcol-head-right">
          {hideCount ? null : <span className="count">{jobs.length}</span>}
        </span>
      </div>
      {toolbar ? <div className="kcol-toolbar">{toolbar}</div> : null}
      <div className="kcol-body">
        {jobs.length ? (
          jobs.map((j, i) =>
            renderItem ? (
              renderItem(j, i)
            ) : (
              <JobCard
                job={j}
                tab={tab}
                showRevisions={showRevisions}
                showPriority={showPriority}
                key={j.id || i}
              />
            ),
          )
        ) : (
          <div className="kcol-empty">{emptyLabel}</div>
        )}
      </div>
    </div>
  );
};
